import React, { forwardRef, useContext } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import ModalContext from './ModalContext'
import BaseModal from './BaseModal'
import Icon from 'common/Icon'
import ModalBackdrop from './ModalBackdrop'
import { MODAL_PORTAL_ROOT_ID } from 'constants/common'

export type ModalProps = {
  show?: boolean
  onHide?: () => void
  baseModalClass?: string
  modalContainerClass?: string
  modalContentClass?: string
  backdropClass?: string
  /** Overrides the backdrop's fully-shown opacity (defaults to 0.8). */
  backdropOpacity?: number
  children?: unknown
}

/**
 * **The Modal Component**.
 ** For Mobile, it renders with a slide animation from the bottom of the screen.
 ** For desktop, it renders at the center of the page.
 * @param show Show state specifies whether the Modal should be shown or not
 * @param onHide a callback to specify what happens when you click outside the modal or its close button. Ideally it should set the show state to false
 * @param baseModalClass styles for the Base Modal. Can add to or override the default BaseModal styles
 * @param modalContainerClass styles for the parent container in which the modal content box resides. Can add to or override the default ModalContainer styles
 * @param modalContentClass styles for the modal content box. Can add to or override the default ModalContent styles
 */

export const Modal: React.FC<ModalProps> = ({
  show = false,
  onHide = () => {
    //no default action
  },
  baseModalClass = '',
  modalContainerClass = '',
  modalContentClass = '',
  backdropClass = '',
  backdropOpacity,
  children,
}) => {
  // Get or create portal root element
  const getOrCreatePortalRoot = () => {
    let portalRoot = document.getElementById(MODAL_PORTAL_ROOT_ID)

    if (!portalRoot) {
      portalRoot = document.createElement('div')
      portalRoot.id = MODAL_PORTAL_ROOT_ID
      document.body.appendChild(portalRoot)
    }

    return portalRoot
  }

  const modalContent = (
    <ModalContext.Provider
      value={{
        show,
        onHide,
        baseModalClass,
        modalContainerClass,
        modalContentClass,
      }}
    >
      <ModalBackdrop
        backdropClass={backdropClass}
        backdropOpacity={backdropOpacity}
      />
      <BaseModal>{children}</BaseModal>
    </ModalContext.Provider>
  )

  // Only render if showing and in browser environment
  if (show && typeof document !== 'undefined') {
    const portalRoot = getOrCreatePortalRoot()

    if (portalRoot) {
      return createPortal(modalContent, portalRoot)
    }
  }

  // If not showing or no portal root, render nothing
  return null
}

type ModalHeaderProps = {
  showCloseButton?: boolean
  showDivider?: boolean
  headerClassName?: string
  closeButtonClassName?: string
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  showCloseButton = true,
  showDivider = true,
  children,
  headerClassName,
  closeButtonClassName = '',
}) => {
  const { onHide } = useContext(ModalContext)
  return (
    <div
      className={classNames(
        'ModalHeader',
        {
          ['ModalHeader__divider']: showDivider,
        },
        headerClassName
      )}
    >
      {children}

      {showCloseButton && (
        <div
          role="button"
          className={classNames(
            'ModalHeader__close_btn c-pointer',
            closeButtonClassName
          )}
          tabIndex={0}
          data-testid="modal-close-btn"
          onClick={onHide}
        >
          <Icon
            icon="close"
            fill="black"
            className="ModalHeader__close_btn__close_icon"
          />
        </div>
      )}
    </div>
  )
}

type ModalBodyProps = {
  className?: string
  children?: unknown
}
export const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(
  ({ className, children }, ref) => {
    return (
      <div ref={ref} className={classNames('ModalBody', `${className}`)}>
        {children}
      </div>
    )
  }
)

type ModalFooterProps = {
  className?: string
  showDivider?: boolean
}

export const ModalFooter: React.FC<ModalFooterProps> = ({
  className = '',
  showDivider = true,
  children,
}) => {
  return (
    <div
      className={classNames(`ModalFooter ${className}`, {
        ['ModalFooter__divider']: showDivider,
      })}
    >
      {children}
    </div>
  )
}
